import { SupportedFullInfoDtoIn } from '@/models/in/SupportedFullInfoDtoIn';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import { defaultFormValues, type SupportedFormData } from '../schemas/supportedFormSchema';

/**
 * HAT-1464: the office deletes a supported mostly because their details were entered wrong, and the
 * coordinator enters them again. Their ID is still taken by the deleted record, so the form opens on
 * that record: the personal details ready to correct, the children and bank accounts listed to keep
 * or drop. Nothing new is pre-filled - no new account, no new child.
 */
export function mapExistingSupportedToFormData(existing: SupportedFullInfoDtoIn): SupportedFormData {
  const { contact } = existing;
  const birthDate = contact.birthDate ? new Date(contact.birthDate) : undefined;
  const existingPaymentInfos = (existing.paymentInfos?.accounts ?? []).map((account) => ({
    id: account.id,
    bankNumber: account.bankNumber,
    bankName: account.bankName ?? null,
    branchNumber: account.branchNumber ?? '',
    accountNumber: account.accountNumber ?? '',
    accountOwnerName: account.accountOwnerName ?? '',
    status: account.status,
  }));

  return {
    ...defaultFormValues,
    contact: {
      ...defaultFormValues.contact,
      id: contact.id,
      israeliId: contact.israeliId,
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      address: {
        ...defaultFormValues.contact.address!,
        streetName: contact.address?.streetName ?? '',
        streetNumber: contact.address?.streetNumber ?? '',
        apartmentNumber: contact.address?.apartmentNumber ?? '',
        city: contact.address?.city ?? '',
        country: contact.address?.country ?? '',
        zipCode: contact.address?.zipCode ?? '',
        pob: contact.address?.pob ?? '',
      },
      birthDate: birthDate && !Number.isNaN(birthDate.getTime()) ? birthDate : undefined,
    },
    existingChildren: (existing.children ?? []).map((child) => ({
      id: child.id,
      firstName: child.firstName ?? '',
      lastName: child.lastName ?? '',
      israeliId: child.israeliId ?? '',
      birthDate: child.birthDate ?? null,
      status: child.status,
    })),
    existingPaymentInfos,
    // A kept account is enough; a blank new one is offered only when there is nothing to keep.
    paymentInfos: existingPaymentInfos.length > 0 ? [] : defaultFormValues.paymentInfos,
  };
}

/**
 * The classifications the supported already holds in this fund. The server refuses a second one of
 * the same type (as the add-classification dialog does), so the form shows these as existing.
 */
export function existingClassificationIdsOf(existing: SupportedFullInfoDtoIn | null | undefined): number[] {
  return (existing?.contactClassifications ?? []).map((cc) => cc.classification.id);
}

/** Existing classifications that still stand - a rejected or cancelled one says nothing about the family. */
export function standingClassificationNamesOf(existing: SupportedFullInfoDtoIn | null | undefined): string[] {
  return (existing?.contactClassifications ?? [])
    .filter((cc) => cc.status !== ContactClassificationStatus.Rejected && cc.status !== ContactClassificationStatus.Cancelled)
    .map((cc) => cc.classification.name);
}
